import { ChartFigure, toTsv } from "@/components/tool-kit/charts/figure";
import type { ComparativeAdvantageStrings } from "@/components/tools/comparative-advantage-calculator";
import {
  EconChart,
  type EconCurve,
  type EconDot,
} from "@/components/tools/econ-micro/curve-chart";
import { t } from "@/lib/i18n/interpolate";
import { chartFilename } from "@/lib/tool-kit/charts/layout";
import { type Producer, tradeLine } from "@/lib/tool-kit/econ-micro/trade";
import { CHART_COLORS } from "@/lib/tool-kit/palette";

type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;

/** One producer's frontier, and the consumption line trade opens up. */
export function TradeGraph({
  index,
  name,
  producer,
  good1,
  good2,
  specializesIn1,
  price,
  strings,
  fmt,
}: {
  index: number;
  name: string;
  producer: Producer;
  good1: string;
  good2: string;
  /** True or false for the good the producer specializes in; null with no gains. */
  specializesIn1: boolean | null;
  price: number | null;
  strings: ComparativeAdvantageStrings;
  fmt: Fmt;
}) {
  const text = (v: number) => fmt(v, { maximumFractionDigits: 2 });
  const trade =
    specializesIn1 !== null && price !== null
      ? tradeLine(producer, specializesIn1, price)
      : null;
  const xMax = Math.max(
    producer.good1,
    trade ? trade[0][0] : 0,
    trade ? trade[1][0] : 0
  );
  const yMax = Math.max(
    producer.good2,
    trade ? trade[0][1] : 0,
    trade ? trade[1][1] : 0
  );
  const curves: EconCurve[] = [
    {
      id: "ppf",
      points: [
        [0, producer.good2],
        [producer.good1, 0],
      ],
      color: CHART_COLORS[0],
      label: strings.curvePpf,
      // Keep its label off the point where the trade line leaves the frontier.
      labelAtStart: specializesIn1 === true,
    },
  ];
  const dots: EconDot[] = [];
  if (trade && specializesIn1 !== null) {
    curves.push({
      id: "trade",
      points: trade,
      color: CHART_COLORS[3],
      dashed: true,
      label: strings.curveTrade,
    });
    dots.push({
      id: "specialize",
      x: trade[0][0],
      y: trade[0][1],
      label: strings.pointSpecialize,
      position: specializesIn1 ? "n" : "e",
      color: CHART_COLORS[3],
    });
  }
  const title = t(strings.graphTitle, { producer: name });
  const head = [strings.tableCurve, good1, good2];
  const rows: (string | number)[][] = [
    [strings.curvePpf, text(producer.good1), "0"],
    [strings.curvePpf, "0", text(producer.good2)],
    ...(trade
      ? trade.map((point) => [
          strings.curveTrade,
          text(point[0]),
          text(point[1]),
        ])
      : []),
  ];
  return (
    <ChartFigure
      apa={false}
      data={toTsv([head, ...rows])}
      figureNumber={String(index + 1)}
      filename={chartFilename(title, "production-possibilities")}
      labels={strings}
      note=""
      table={{ head, rows }}
      title={title}
    >
      <EconChart
        curves={curves}
        description={t(strings.graphDescription, {
          producer: name,
          good1,
          good2,
          x: text(producer.good1),
          y: text(producer.good2),
        })}
        dots={dots}
        fmt={fmt}
        id={`ca-chart-${index}`}
        label={title}
        title={title}
        xMax={xMax}
        xTitle={good1}
        yMax={yMax}
        yTitle={good2}
      />
    </ChartFigure>
  );
}
